import { formatUomName } from "@/lib/uom";
import type { DecimalPrecision } from "@/utils/decimal";
import type { DeliveryOrder } from "@/types/salesDocuments";
import {
  formatCurrency,
  formatTotalCurrency,
  formatDate,
  formatDateTime,
  getImageDataUrl,
  getPrintStyles,
  printHtml,
} from "./printHelpers";

interface DeliveryOrderPrintData {
  /** Company decimal precision. */
  precision: DecimalPrecision | number;
  deliveryOrder: DeliveryOrder;
  companyInfo: {
    businessName: string;
    addressLine0: string;
    addressLine1?: string;
    addressLine2?: string;
    cityName: string;
    postalZone: string;
    state: string;
    country: string;
    contactNumber: string;
    email: string;
    tin: string;
    registrationNumber: string;
    companyLogo?: string;
    reportFooter?: string;
  };
}

const getStatusClass = (status: string): string => {
  const statusMap: Record<string, string> = {
    Draft: "status-draft",
    Pending: "status-sent",
    "In Transit": "status-processing",
    Delivered: "status-delivered",
    Cancelled: "status-cancelled",
  };
  return statusMap[status] || "status-draft";
};

const getStatusLabel = (status: string): string => {
  return status.charAt(0).toUpperCase() + status.slice(1);
};

export const generateDeliveryOrderHtml = async (
  data: DeliveryOrderPrintData
): Promise<string> => {
  const { deliveryOrder, companyInfo, precision } = data;

  // Get company logo if available
  let logoDataUrl = "";
  if (companyInfo.companyLogo) {
    try {
      logoDataUrl = await getImageDataUrl(
        `${import.meta.env.VITE_BACKEND_API_BASE_URL}/company-logo/${companyInfo.companyLogo}`
      );
    } catch (error) {
      console.error("Error loading company logo:", error);
    }
  }

  // Build company address
  const companyAddress = [
    companyInfo.addressLine0,
    companyInfo.addressLine1,
    companyInfo.addressLine2,
    companyInfo.postalZone,
    companyInfo.cityName,
    companyInfo.state,
    companyInfo.country,
  ]
    .filter(Boolean)
    .join(", ");

  // Build buyer address
  const buyerAddress = [
    deliveryOrder.buyerAddressLine0,
    deliveryOrder.buyerAddressLine1,
    deliveryOrder.buyerAddressLine2,
    deliveryOrder.buyerPostcode,
    deliveryOrder.buyerCity,
    deliveryOrder.buyerState,
    deliveryOrder.buyerCountry,
  ]
    .filter(Boolean)
    .join(", ");

  // Generate line items HTML
  const lineItemsHtml = deliveryOrder.lineItems
    .map(
      (item, index) => `
    <tr>
      <td class="text-center">${index + 1}</td>
      <td>${item.itemCode || ""}</td>
      <td>
        <strong>${item.description}</strong>
        ${item.classification ? `<br/><small>Class: ${item.classification}</small>` : ""}
      </td>
      <td class="text-center">${item.orderedQuantity || 0}</td>
      <td class="text-center">${item.availableQuantity || 0}</td>
      <td class="text-center"><strong>${item.quantity}</strong></td>
      <td class="text-center">${formatUomName(item.measurement) || "Unit"}</td>
      <td class="text-right">${formatCurrency(item.unitPrice, deliveryOrder.currencyCode, precision)}</td>
      <td class="text-right"><strong>${formatCurrency(item.total, deliveryOrder.currencyCode, precision)}</strong></td>
    </tr>
  `
    )
    .join("");

  // Check if there's delivery information
  const hasDeliveryInfo =
    deliveryOrder.carrierName ||
    deliveryOrder.trackingNumber ||
    deliveryOrder.vehicleNumber ||
    deliveryOrder.driverName ||
    deliveryOrder.driverContact;

  const html = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Delivery Order ${deliveryOrder.codeNumber}</title>
      ${getPrintStyles()}
    </head>
    <body>
      <div class="container">
        <!-- Header -->
        <div class="header">
          <div class="company-info">
            ${logoDataUrl ? `<img src="${logoDataUrl}" alt="Company Logo" class="company-logo"/>` : ""}
            <div class="company-name">${companyInfo.businessName}</div>
            <div class="company-details">
              <div>${companyAddress}</div>
              <div>Tel: ${companyInfo.contactNumber} | Email: ${companyInfo.email}</div>
              <div>TIN: ${companyInfo.tin} | Reg No: ${companyInfo.registrationNumber}</div>
            </div>
          </div>
          <div class="document-title">
            <div class="document-type">DELIVERY ORDER</div>
            <div class="document-number">${deliveryOrder.codeNumber}</div>
            <div class="document-date">Delivery Date: ${formatDate(deliveryOrder.deliveryDate)}</div>
            ${deliveryOrder.salesOrderNumber ? `<div class="document-date">Sales Order: ${deliveryOrder.salesOrderNumber}</div>` : ""}
            <div class="status-badge ${getStatusClass(deliveryOrder.status)}">
              ${getStatusLabel(deliveryOrder.status)}
            </div>
          </div>
        </div>

        <!-- Delivery To Information -->
        <div class="parties-section">
          <div class="party-box">
            <div class="party-title">DELIVER TO</div>
            <div class="party-details">
              <div><strong>Name:</strong> ${deliveryOrder.buyerName}</div>
              ${deliveryOrder.buyerTin ? `<div><strong>TIN:</strong> ${deliveryOrder.buyerTin}</div>` : ""}
              ${deliveryOrder.buyerRegistrationNumber ? `<div><strong>Reg No:</strong> ${deliveryOrder.buyerRegistrationNumber}</div>` : ""}
              <div><strong>Address:</strong> ${buyerAddress}</div>
              ${deliveryOrder.buyerContact ? `<div><strong>Tel:</strong> ${deliveryOrder.buyerContact}</div>` : ""}
              ${deliveryOrder.buyerEmail ? `<div><strong>Email:</strong> ${deliveryOrder.buyerEmail}</div>` : ""}
            </div>
          </div>
        </div>

        <!-- Delivery Information -->
        ${
          hasDeliveryInfo
            ? `
        <div class="delivery-info">
          <div class="delivery-info-title">DELIVERY DETAILS</div>
          <div class="delivery-grid">
            ${deliveryOrder.carrierName ? `<div><strong>Carrier:</strong> ${deliveryOrder.carrierName}</div>` : ""}
            ${deliveryOrder.trackingNumber ? `<div><strong>Tracking No:</strong> ${deliveryOrder.trackingNumber}</div>` : ""}
            ${deliveryOrder.vehicleNumber ? `<div><strong>Vehicle No:</strong> ${deliveryOrder.vehicleNumber}</div>` : ""}
            ${deliveryOrder.driverName ? `<div><strong>Driver:</strong> ${deliveryOrder.driverName}</div>` : ""}
            ${deliveryOrder.driverContact ? `<div><strong>Driver Contact:</strong> ${deliveryOrder.driverContact}</div>` : ""}
          </div>
          ${
            deliveryOrder.deliveryInstructions
              ? `
          <div style="margin-top: 8px;">
            <strong>Delivery Instructions:</strong>
            <div style="margin-top: 4px;">${deliveryOrder.deliveryInstructions}</div>
          </div>
          `
              : ""
          }
        </div>
        `
            : ""
        }

        <!-- Line Items Table -->
        <table class="items-table">
          <thead>
            <tr>
              <th style="width: 30px;">#</th>
              <th style="width: 90px;">Item Code</th>
              <th>Description</th>
              <th style="width: 65px;">Ordered</th>
              <th style="width: 65px;">Available</th>
              <th style="width: 75px;">Delivering</th>
              <th style="width: 50px;">UOM</th>
              <th style="width: 90px;">Unit Price</th>
              <th style="width: 100px;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${lineItemsHtml}
          </tbody>
        </table>

        <!-- Totals Section -->
        <div class="totals-section">
          <table class="totals-table">
            <tr>
              <td>Subtotal (Excl. Tax):</td>
              <td>${formatTotalCurrency(deliveryOrder.totalExcludingTax, deliveryOrder.currencyCode)}</td>
            </tr>
            <tr>
              <td>Total Tax Amount:</td>
              <td>${formatTotalCurrency(deliveryOrder.totalTaxAmount, deliveryOrder.currencyCode)}</td>
            </tr>
            <tr>
              <td>Total (Incl. Tax):</td>
              <td>${formatTotalCurrency(deliveryOrder.totalIncludingTax, deliveryOrder.currencyCode)}</td>
            </tr>
            <tr class="total-row">
              <td>TOTAL VALUE:</td>
              <td>${formatTotalCurrency(deliveryOrder.totalPayableAmount, deliveryOrder.currencyCode)}</td>
            </tr>
          </table>
        </div>

        <!-- Remarks -->
        ${
          deliveryOrder.remarks
            ? `
        <div class="footer-section">
          <div class="footer-title">Remarks</div>
          <div class="footer-content">${deliveryOrder.remarks}</div>
        </div>
        `
            : ""
        }

        <!-- Signature Section -->
        <div class="footer-section" style="margin-top: 40px;">
          <div style="display: flex; justify-content: space-between;">
            <div style="flex: 1; text-align: center; padding: 0 20px;">
              <div style="border-top: 1px solid #333; padding-top: 8px; margin-top: 60px;">
                <strong>Prepared By</strong>
              </div>
              <div style="margin-top: 4px; font-size: 9pt; color: #6b7280;">
                Name & Signature
              </div>
            </div>
            <div style="flex: 1; text-align: center; padding: 0 20px;">
              <div style="border-top: 1px solid #333; padding-top: 8px; margin-top: 60px;">
                <strong>Received By</strong>
              </div>
              <div style="margin-top: 4px; font-size: 9pt; color: #6b7280;">
                Name, Signature & Date
              </div>
            </div>
          </div>
        </div>

        <!-- Report Footer -->
        ${
          companyInfo.reportFooter
            ? `
        <div class="footer-section">
          <div class="footer-content">${companyInfo.reportFooter}</div>
        </div>
        `
            : ""
        }

        <!-- Footer Note -->
        <div class="footer-section" style="margin-top: 30px; font-size: 8pt; color: #6b7280; text-align: center;">
          <p>This is a computer-generated document.</p>
          <p>Generated on ${formatDateTime(new Date())}</p>
        </div>
      </div>
    </body>
    </html>
  `;

  return html;
};

export const printDeliveryOrder = async (data: DeliveryOrderPrintData) => {
  const html = await generateDeliveryOrderHtml(data);
  printHtml(html, `DeliveryOrder_${data.deliveryOrder.codeNumber}`);
};
